import { dayName } from './clock'
import { t, type StaffLanguage } from './staffText'
import type { Ticket } from './types'
import type { Estimate } from './estimate'

/**
 * What reception's Find says, from what the server found.
 *
 * The server answers with today's ticket with the number and every one still open
 * with it from any day (api.foundByNumber), today's first. One is the answer. More
 * than one is a question for the person at the desk — a day nobody ended left a
 * customer in the hall with a number that was issued again this morning — so they
 * are all shown with their dates and the desk picks the slip in front of it.
 */
export interface FindAnswer {
  found: Ticket | null
  /** Several open tickets share the number; empty unless there are two or more. */
  choices: Ticket[]
  /** The sentence for nothing found; empty otherwise. */
  missing: string
}

export function findAnswer(tickets: readonly Ticket[], number: string, lang: StaffLanguage): FindAnswer {
  if (tickets.length === 0) {
    return { found: null, choices: [], missing: t('reception.noTicketToday', lang, { number }) }
  }
  if (tickets.length === 1) return { found: tickets[0]!, choices: [], missing: '' }
  return { found: null, choices: [...tickets], missing: '' }
}

/** "from Monday 22 September" for a ticket of another business day, '' for one of
 *  today's — the date is only worth reading when it is not the one assumed. */
export function issuedOn(
  ticket: Pick<Ticket, 'businessDate'>,
  today: string | undefined,
  lang: StaffLanguage,
): string {
  if (!ticket.businessDate || ticket.businessDate === today) return ''
  return t('reception.issuedFrom', lang, { day: dayName(ticket.businessDate, lang) })
}

/** The position and wait a search answered with belong to the first ticket it
 *  found; they are this ticket's only when that is this ticket. */
export function placeFor(
  ticketId: string,
  answer: { tickets: Ticket[]; position: number; estimate?: Estimate | null },
): { position?: number; estimate?: Estimate } {
  if (answer.tickets[0]?.id !== ticketId) return {}
  return { position: answer.position, ...(answer.estimate && { estimate: answer.estimate }) }
}
